<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">
            Détail de l’achat
            <span v-if="purchase" class="text-muted">#{{ purchase.reference }}</span>
          </h1>
          <div v-if="purchase" class="small text-muted">
            Créé le {{ $format.datetime(purchase.created_at) }}
            <span v-if="purchase.creator_name">
              par {{ purchase.creator_name }}
            </span>
          </div>
        </div>

        <div class="d-flex gap-2">
          <button class="btn btn-outline-secondary btn-sm btn-icon-label" title="Retour à la liste" @click="$router.push({ name: 'Purchases' })">
            <i class="fa fa-arrow-left"></i>
            <span class="btn-label ms-1">Retour à la liste</span>
          </button>

          <button
            v-if="canEditPurchase"
            type="button"
            class="btn btn-outline-primary btn-sm btn-icon-label"
            title="Modifier"
            @click="openEditDialog"
          >
            <i class="fa fa-pen"></i>
            <span class="btn-label ms-1">Modifier</span>
          </button>

          <button
            v-if="canValidatePurchase"
            type="button"
            class="btn btn-outline-success btn-sm btn-icon-label"
            :disabled="isValidating || !hasPurchaseItems"
            title="Valider"
            @click="validatePurchase"
          >
            <i class="fa fa-check"></i>
            <span class="btn-label ms-1">Valider</span>
          </button>

          <AppLoadingButton
            v-if="canDeletePurchase"
            type="button"
            class="btn btn-outline-danger btn-sm btn-icon-label"
            :loading="isDeletingPurchase"
            loading-label="Suppression..."
            title="Supprimer"
            @click="deletePurchase"
          >
            <i class="fa fa-trash"></i>
            <span class="btn-label ms-1">Supprimer</span>
          </AppLoadingButton>

          <button
            type="button"
            class="btn btn-outline-primary btn-sm btn-icon-label"
            :disabled="!hasPurchaseItems"
            title="Aperçu bon de commande"
            @click="openPurchaseOrderPreview"
          >
            <i class="fa fa-file-invoice"></i>
            <span class="btn-label ms-1">Aperçu bon de commande</span>
          </button>

          <button
            v-if="canCreateSupplierReturn"
            type="button"
            class="btn btn-outline-warning btn-sm btn-icon-label"
            title="Créer un retour fournisseur"
            @click="confirmSupplierReturnCreate"
          >
            <i class="fa fa-rotate-left"></i>
            <span class="btn-label ms-1">Retour fournisseur</span>
          </button>

        </div>
      </div>

      <div v-if="successMessage" class="alert alert-success mb-3 py-2">
        {{ successMessage }}
      </div>

      <!-- Loading / erreur -->
      <div v-if="isLoading" class="text-center my-5">
        <i class="fa fa-spinner fa-spin fa-2x mb-2"></i>
        <div class="small text-muted">Chargement de l’achat...</div>
      </div>

      <div v-else-if="errorMessage" class="alert alert-danger">
        {{ errorMessage }}
      </div>

      <div v-else-if="!purchase && !errorMessage" class="alert alert-warning">
        Achat introuvable.
      </div>

      <!-- Contenu principal -->
      <div v-else-if="purchase" class="row g-3 align-items-start">
        <!-- Carte informations générales -->
        <div class="col-lg-4">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-0 pb-1">
              <h6 class="mb-0">Informations générales</h6>
            </div>
            <div class="card-body pt-2 pb-3">
              <div class="mb-2 d-flex justify-content-between align-items-center">
                <span class="small text-muted">Référence</span>
                <span class="fw-semibold">{{ purchase.reference }}</span>
              </div>

              <div class="mb-2">
                <div class="small text-muted">Fournisseur</div>
                <div class="fw-semibold">{{ purchase.supplier_name }}</div>
              </div>

              <div class="mb-2 d-flex justify-content-between">
                <span class="small text-muted">Date d’achat</span>
                <span class="fw-semibold">
                  {{ $format.date(purchase.purchase_date) }}
                </span>
              </div>

              <div class="mb-2 d-flex justify-content-between align-items-center">
                <span class="small text-muted">Statut</span>
                <span class="badge" :class="statusClass(purchase.status)">
                  {{ statusLabel(purchase.status) }}
                </span>
              </div>

              <div class="mb-2 d-flex justify-content-between">
                <span class="small text-muted">Montant total</span>
                <span class="fw-bold">
                  {{ $format.currency(purchase.total_amount) }}
                </span>
              </div>

              <div class="mb-2 d-flex justify-content-between align-items-center">
                <span class="small text-muted">Règlement</span>
                <span class="badge" :class="paymentStatusClass(purchase.payment_status)">
                  {{ paymentStatusLabel(purchase.payment_status) }}
                </span>
              </div>

              <div class="mb-2 d-flex justify-content-between">
                <span class="small text-muted">Payé</span>
                <span class="fw-semibold">{{ $format.currency(purchase.paid_amount || 0) }}</span>
              </div>

              <div class="mb-2 d-flex justify-content-between">
                <span class="small text-muted">Reste dû</span>
                <span class="fw-bold text-danger">{{ $format.currency(purchase.due_amount || 0) }}</span>
              </div>

              <div v-if="purchase.validated_at" class="mb-2">
                <div class="small text-muted">Validé le</div>
                <div class="fw-semibold">
                  {{ $format.datetime(purchase.validated_at) }}
                  <span v-if="purchase.validator_name">
                    par {{ purchase.validator_name }}
                  </span>
                </div>
              </div>

              <div v-if="purchase.comment" class="mt-2">
                <div class="small text-muted">Commentaire</div>
                <div>{{ purchase.comment }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Lignes d'achat -->
        <div class="col-lg-8">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="mb-0">Lignes d’achat</h6>
            <small v-if="hasPurchaseItems" class="text-muted">
              {{ purchaseItems.length }} ligne(s)
            </small>
          </div>

          <BootstrapDataTable
            :rows="purchaseItems"
            :columns="purchaseLineColumns"
            row-key="id"
            empty-text="Aucune ligne d’achat."
            client-pagination
            :per-page="10"
            :show-refresh="false"
            :show-export="false"
            :show-print="false"
          >
            <template #cell-product_name="{ row }">
              <div class="fw-semibold">{{ row.product_name }}</div>
            </template>

            <template #cell-product_sku="{ row }">
              {{ row.product_sku || '-' }}
            </template>

            <template #cell-unit_price="{ row }">
              {{ $format.currency(row.unit_price || 0) }}
            </template>

            <template #cell-lot_number="{ row }">
              {{ row.lot_number || '-' }}
            </template>

            <template #cell-expiry_date="{ row }">
              {{ row.expiry_date ? $format.date(row.expiry_date) : '-' }}
            </template>

            <template #cell-line_total="{ row }">
              {{ $format.currency(row.line_total || 0) }}
            </template>

            <template #cell-label_actions="{ row }">
              <button
                type="button"
                class="btn btn-outline-primary btn-sm btn-action-icon"
                :disabled="!labelBarcodeForLine(row)"
                title="Imprimer étiquette"
                aria-label="Imprimer étiquette"
                @click.stop="openLabelDialog(row)"
              >
                <i class="fa fa-barcode"></i>
              </button>
            </template>
          </BootstrapDataTable>

          <div
            v-if="hasPurchaseItems"
            class="purchase-lines-total d-flex justify-content-end align-items-center gap-3 mt-2"
          >
            <span class="fw-semibold">Total</span>
            <span class="fw-bold">{{ $format.currency(purchase.total_amount || 0) }}</span>
          </div>
        </div>

        <!-- Règlements fournisseur -->
        <div class="col-lg-12">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-0 pb-1 d-flex justify-content-between align-items-center">
              <div>
                <h6 class="mb-0">Règlements fournisseur</h6>
                <small v-if="purchase.status === 'validated'" class="text-muted">
                  Payé {{ $format.currency(purchase.paid_amount || 0) }} · Reste {{ $format.currency(purchase.due_amount || 0) }}
                </small>
                <small v-else class="text-muted">
                  Les règlements seront disponibles après validation de la commande.
                </small>
              </div>
              <button
                v-if="canAddSupplierPayment"
                type="button"
                class="btn btn-outline-primary btn-sm btn-icon-label"
                @click="openPaymentDialog"
              >
                <i class="fa fa-plus"></i>
                <span class="btn-label ms-1">Ajouter règlement</span>
              </button>
            </div>
            <div class="card-body pt-2 pb-3">
              <div v-if="purchase.status !== 'validated'" class="alert alert-info mb-0 py-2">
                Validez d'abord cette commande pour enregistrer un paiement fournisseur.
              </div>
              <div v-else class="table-responsive">
                <table class="table table-sm table-striped table-hover table-bordered align-middle mb-0">
                  <thead class="table-light">
                    <tr>
                      <th>Date</th>
                      <th>Montant</th>
                      <th>Méthode</th>
                      <th>Référence</th>
                      <th>Agent</th>
                      <th class="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!purchase.payments || !purchase.payments.length">
                      <td colspan="6" class="text-center text-muted py-3">
                        Aucun règlement enregistré.
                      </td>
                    </tr>
                    <tr v-for="payment in purchase.payments" :key="payment.id">
                      <td>{{ $format.date(payment.payment_date) }}</td>
                      <td class="fw-semibold">{{ $format.currency(payment.amount || 0) }}</td>
                      <td>{{ payment.method || '-' }}</td>
                      <td>{{ payment.reference || '-' }}</td>
                      <td>{{ payment.user?.name || payment.user?.username || '-' }}</td>
                      <td class="text-end">
                        <AppLoadingButton
                          type="button"
                          class="btn btn-outline-danger btn-sm btn-action-icon"
                          :loading="isDeletingPayment(payment)"
                          loading-label=""
                          title="Supprimer le règlement"
                          @click="deletePayment(payment)"
                        >
                          <i class="fa fa-trash"></i>
                        </AppLoadingButton>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Dialog édition -->
      <PrimevueDialog
        v-model:visible="formDialogVisible"
        :modal="true"
        :style="{ width: '80vw', maxWidth: '95vw' }"
        :breakpoints="{ '768px': '95vw' }"
        :closable="false"
      >
        <template #header>
          <div class="d-flex align-items-center justify-content-between gap-2 w-100">
            <h5 class="mb-0">
              Modifier l'achat
              <span v-if="purchase?.reference" class="text-muted fw-normal ms-1">
                #{{ purchase.reference }}
              </span>
            </h5>
            <button
              type="button"
              class="btn btn-outline-primary btn-sm btn-action-icon"
              title="Ouvrir la modification dans une page"
              aria-label="Ouvrir la modification dans une page"
              :disabled="!purchase?.id"
              @click="openPurchaseEditPage"
            >
              <i class="fa fa-up-right-from-square"></i>
            </button>
          </div>
        </template>

        <PurchaseForm
          v-if="purchase"
          :mode="'edit'"
          :purchase="purchase"
          @save="handleSave"
          @cancel="closeFormDialog"
          @suspend="handlePurchaseSuspend"
          @finish="handlePurchaseFinish"
        />
      </PrimevueDialog>

      <SupplierPriceChangesDialog
        v-model:visible="supplierPriceChangesDialogVisible"
        :changes="supplierPriceChanges"
        :saving="isValidating"
        @confirm="confirmValidationWithSupplierPrices"
      />

      <PrimevueDialog
        v-model:visible="purchaseOrderDialogVisible"
        :modal="true"
        :style="{ width: '900px', maxWidth: '100%' }"
        header="Aperçu bon de commande"
      >
        <div id="printable-purchase-order" v-if="purchase" class="purchase-order-preview">
          <div class="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h4 class="mb-1">{{ shopInfo.name || 'Suite241 Gestion Commerciale' }}</h4>
              <div class="small text-muted">
                <div v-if="shopInfo.legal_name">{{ shopInfo.legal_name }}</div>
                <div>{{ [shopInfo.address, shopInfo.city, shopInfo.country].filter(Boolean).join(', ') }}</div>
                <div v-if="shopInfo.phone">{{ shopInfo.phone }}</div>
                <div v-if="shopInfo.email">{{ shopInfo.email }}</div>
              </div>
            </div>
            <img v-if="shopInfo.logo_url" :src="shopInfo.logo_url" alt="Logo" class="purchase-order-logo" />
          </div>

          <div class="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h5 class="mb-1">Bon de commande</h5>
              <div>Référence : <strong>{{ purchase.reference }}</strong></div>
              <div>Date : {{ $format.date(purchase.purchase_date) }}</div>
              <div>Statut règlement : {{ paymentStatusLabel(purchase.payment_status) }}</div>
            </div>
            <div class="text-end">
              <div class="small text-muted">Fournisseur</div>
              <strong>{{ purchase.supplier_name }}</strong>
              <div v-if="purchase.supplier?.phone">{{ purchase.supplier.phone }}</div>
              <div v-if="purchase.supplier?.email">{{ purchase.supplier.email }}</div>
            </div>
          </div>

          <table class="table table-sm table-striped table-bordered align-middle">
            <thead class="table-light">
              <tr>
                <th>Produit</th>
                <th>Référence</th>
                <th class="text-end">Qté</th>
                <th class="text-end">PU</th>
                <th>Lot</th>
                <th>Expiration</th>
                <th class="text-end">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in purchase.items" :key="item.id">
                <td>{{ item.product_name }}</td>
                <td>{{ item.product_sku || '-' }}</td>
                <td class="text-end">{{ item.quantity }}</td>
                <td class="text-end">{{ $format.currency(item.unit_price || 0) }}</td>
                <td>{{ item.lot_number || '-' }}</td>
                <td>{{ item.expiry_date ? $format.date(item.expiry_date) : '-' }}</td>
                <td class="text-end fw-semibold">{{ $format.currency(item.line_total || 0) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="6" class="text-end fw-semibold">Total</td>
                <td class="text-end fw-bold">{{ $format.currency(purchase.total_amount || 0) }}</td>
              </tr>
              <tr>
                <td colspan="6" class="text-end">Payé</td>
                <td class="text-end">{{ $format.currency(purchase.paid_amount || 0) }}</td>
              </tr>
              <tr>
                <td colspan="6" class="text-end">Reste dû</td>
                <td class="text-end fw-bold">{{ $format.currency(purchase.due_amount || 0) }}</td>
              </tr>
            </tfoot>
          </table>
          <div class="text-center text-muted mt-3" style="font-size: 8px">powered by Suite241</div>
        </div>

        <template #footer>
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="purchaseOrderDialogVisible = false">
            <i class="fa fa-xmark me-1" aria-hidden="true"></i>
            Fermer
          </button>
          <button type="button" class="btn btn-primary btn-sm" @click="printPurchaseOrder">
            <i class="fa fa-print me-1"></i>
            Imprimer
          </button>
        </template>
      </PrimevueDialog>

      <PrimevueDialog
        v-model:visible="paymentDialogVisible"
        :modal="true"
        :style="{ width: '460px', maxWidth: '100%' }"
        header="Ajouter un règlement fournisseur"
      >
        <div class="mb-3">
          <label class="form-label">Montant</label>
          <input v-model.number="paymentForm.amount" type="number" min="0.01" step="0.01" class="form-control" />
          <div class="form-text">Reste dû : {{ $format.currency(purchase?.due_amount || 0) }}</div>
        </div>
        <div class="mb-3">
          <label class="form-label">Date</label>
          <input v-model="paymentForm.payment_date" type="date" class="form-control" />
        </div>
        <div class="mb-3">
          <label class="form-label">Méthode</label>
          <input v-model="paymentForm.method" type="text" class="form-control" placeholder="Espèces, mobile money, virement..." />
        </div>
        <div class="mb-3">
          <label class="form-label">Référence</label>
          <input v-model="paymentForm.reference" type="text" class="form-control" />
        </div>
        <div class="mb-0">
          <label class="form-label">Notes</label>
          <textarea v-model="paymentForm.notes" rows="3" class="form-control"></textarea>
        </div>

        <template #footer>
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isSavingPayment" @click="paymentDialogVisible = false">
            <i class="fa fa-xmark me-1" aria-hidden="true"></i>
            Fermer
          </button>
          <AppLoadingButton
            type="button"
            class="btn btn-primary btn-sm"
            :loading="isSavingPayment"
            loading-label="Enregistrement..."
            @click="submitPayment"
          >
            <i class="fa fa-save me-1"></i>
            Enregistrer
          </AppLoadingButton>
        </template>
      </PrimevueDialog>

      <ProductLabelPrintDialog
        v-model:visible="labelDialogVisible"
        :product-name="selectedLabelProductName"
        :sale-price="selectedLabelSalePrice"
        :barcode="selectedLabelBarcode"
        :quantity="selectedLabelQuantity"
        show-quantity-choice
      />
    </template>
  </AdminLayout>
</template>

<script>
import ProductLabelPrintDialog from '@/components/ProductLabelPrintDialog.vue';
import PurchaseForm from './PurchaseForm.vue';
import SupplierPriceChangesDialog from './SupplierPriceChangesDialog.vue';

export default {
  name: 'PurchaseShowView',
  components: {
    ProductLabelPrintDialog,
    PurchaseForm,
    SupplierPriceChangesDialog,
  },
  data() {
    return {
      purchase: null,
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      formDialogVisible: false,
      supplierPriceChangesDialogVisible: false,
      supplierPriceChanges: [],
      isValidating: false,
      isDeletingPurchase: false,
      deletingPaymentId: null,
      purchaseOrderDialogVisible: false,
      labelDialogVisible: false,
      selectedLabelLine: null,
      paymentDialogVisible: false,
      isSavingPayment: false,
      paymentForm: {
        amount: null,
        payment_date: new Date().toISOString().slice(0, 10),
        method: '',
        reference: '',
        notes: '',
      },
    };
  },
  computed: {
    shopInfo() {
      return this.$store.getters['shop/shopInfo'] || {};
    },
    purchaseItems() {
      return this.purchase?.items || [];
    },
    hasPurchaseItems() {
      return this.purchaseItems.length > 0;
    },
    canEditPurchase() {
      return this.purchase?.status === 'draft';
    },
    canValidatePurchase() {
      return this.purchase?.status === 'draft';
    },
    canDeletePurchase() {
      return this.purchase?.status === 'draft';
    },
    canCreateSupplierReturn() {
      return this.purchase?.status === 'validated';
    },
    canAddSupplierPayment() {
      return this.purchase?.status === 'validated' && Number(this.purchase?.due_amount || 0) > 0;
    },
    purchaseLineColumns() {
      const columns = [
        { key: 'product_name', label: 'Produit', style: 'width: 35%;' },
        { key: 'product_sku', label: 'Référence' },
        { key: 'quantity', label: 'Qté', class: 'text-end' },
        { key: 'unit_price', label: 'PU achat', class: 'text-end' },
        { key: 'lot_number', label: 'Lot' },
        { key: 'expiry_date', label: 'Expiration' },
        { key: 'line_total', label: 'Total ligne', class: 'text-end fw-semibold' },
      ];

      if (this.purchase?.status === 'validated') {
        columns.push({ key: 'label_actions', label: 'Étiquette', class: 'text-end' });
      }

      return columns;
    },
    selectedLabelProductName() {
      return this.labelProductNameForLine(this.selectedLabelLine);
    },
    selectedLabelSalePrice() {
      return this.labelSalePriceForLine(this.selectedLabelLine);
    },
    selectedLabelBarcode() {
      return this.labelBarcodeForLine(this.selectedLabelLine);
    },
    selectedLabelQuantity() {
      return this.labelQuantityForLine(this.selectedLabelLine);
    },
  },
  mounted() {
    this.fetchPurchase();
  },
  beforeUnmount() {
    this.cleanupPurchaseOrderPrintMode();
  },
  methods: {
    async fetchPurchase() {
      const id = this.$route.params.id;

      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';
      this.purchase = null;

      try {
        const res = await this.$api.get(`/purchases/${id}`);
        this.purchase = res.data.data;
      } catch (error) {
        console.error('Erreur chargement achat', error);
        this.errorMessage = 'Impossible de charger cet achat.';
      } finally {
        this.isLoading = false;
      }
    },

    statusLabel(status) {
      return status === 'validated' ? 'Validé' : 'Brouillon';
    },

    statusClass(status) {
      return status === 'validated' ? 'bg-success' : 'bg-secondary';
    },

    paymentStatusLabel(status) {
      if (status === 'paid') return 'Payé';
      if (status === 'partial') return 'Partiel';
      return 'Non payé';
    },

    paymentStatusClass(status) {
      if (status === 'paid') return 'bg-success';
      if (status === 'partial') return 'bg-warning text-dark';
      return 'bg-danger';
    },

    openPaymentDialog() {
      if (!this.purchase) return;
      if (this.purchase.status !== 'validated') {
        this.errorMessage = 'Les règlements sont autorisés uniquement sur les achats validés.';
        return;
      }
      this.paymentForm = {
        amount: this.purchase.due_amount || null,
        payment_date: new Date().toISOString().slice(0, 10),
        method: '',
        reference: '',
        notes: '',
      };
      this.paymentDialogVisible = true;
    },

    async submitPayment() {
      if (!this.purchase) return;
      if (this.isSavingPayment) return;

      this.isSavingPayment = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        await this.$api.post(`/purchases/${this.purchase.id}/payments`, {
          amount: this.paymentForm.amount,
          payment_date: this.paymentForm.payment_date || null,
          method: this.paymentForm.method || null,
          reference: this.paymentForm.reference || null,
          notes: this.paymentForm.notes || null,
        });
        this.paymentDialogVisible = false;
        this.successMessage = 'Règlement fournisseur enregistré.';
        await this.fetchPurchase();
      } catch (error) {
        const errors = error.response?.data?.errors;
        this.errorMessage = errors
          ? Object.values(errors).flat().join(' ')
          : "Une erreur est survenue lors de l'enregistrement du règlement.";
      } finally {
        this.isSavingPayment = false;
      }
    },

    async deletePayment(payment) {
      if (!payment) return;
      if (this.isDeletingPayment(payment)) return;

      this.deletingPaymentId = payment.id;

      const ok = await this.$confirmAction('Supprimer ce règlement fournisseur ?');
      if (!ok) {
        this.deletingPaymentId = null;
        return;
      }

      this.errorMessage = '';
      this.successMessage = '';

      try {
        await this.$api.delete(`/supplier-payments/${payment.id}`);
        this.successMessage = 'Règlement fournisseur supprimé.';
        await this.fetchPurchase();
      } catch (error) {
        const errors = error.response?.data?.errors;
        this.errorMessage = errors
          ? Object.values(errors).flat().join(' ')
          : 'Une erreur est survenue lors de la suppression du règlement.';
      } finally {
        this.deletingPaymentId = null;
      }
    },

    isDeletingPayment(payment) {
      return Number(this.deletingPaymentId || 0) === Number(payment?.id || 0);
    },

    printPurchaseOrder() {
      document.body.classList.add('suite241-printing-purchase-order');
      window.addEventListener('afterprint', this.cleanupPurchaseOrderPrintMode, { once: true });
      window.print();
      window.setTimeout(this.cleanupPurchaseOrderPrintMode, 500);
    },

    cleanupPurchaseOrderPrintMode() {
      document.body.classList.remove('suite241-printing-purchase-order');
    },

    openPurchaseOrderPreview() {
      if (!this.hasPurchaseItems) return;
      this.purchaseOrderDialogVisible = true;
    },

    async confirmSupplierReturnCreate() {
      if (!this.purchase || this.purchase.status !== 'validated') return;

      const confirmed = await this.$confirmAction({
        header: 'Créer un retour fournisseur',
        message: `Créer un retour fournisseur pour l'achat "${this.purchase.reference}" ? Vous allez sélectionner les produits et quantités à retourner. Après validation du retour, le stock sera ajusté et le suivi fournisseur sera mis à jour.`,
        acceptLabel: 'Continuer vers le retour fournisseur',
        rejectLabel: 'Annuler',
        icon: 'pi pi-exclamation-triangle',
      });

      if (!confirmed) return;

      this.$router.push({ name: 'SupplierReturnCreate', query: { purchase_id: this.purchase.id } });
    },

    openLabelDialog(row) {
      if (!row || this.purchase?.status !== 'validated') return;

      this.selectedLabelLine = row;
      this.labelDialogVisible = true;
    },

    labelProductNameForLine(row) {
      return row?.product?.name || row?.product_name || 'Produit';
    },

    labelSalePriceForLine(row) {
      return row?.product?.sale_price ?? row?.sale_price ?? 0;
    },

    labelBarcodeForLine(row) {
      return row?.product?.barcode || row?.barcode || '';
    },

    labelQuantityForLine(row) {
      return Number(row?.quantity || 1);
    },

    openEditDialog() {
      if (!this.purchase) return;
      if (this.purchase.status !== 'draft') return;
      this.$router.push({ name: 'PurchaseEdit', params: { id: this.purchase.id } });
    },

    closeFormDialog() {
      this.formDialogVisible = false;
    },

    openPurchaseEditPage() {
      if (!this.purchase?.id) return;

      this.formDialogVisible = false;
      this.$router.push({ name: 'PurchaseEdit', params: { id: this.purchase.id } });
    },

    async handlePurchaseSuspend(syncedPurchase) {
      if (syncedPurchase) {
        this.purchase = syncedPurchase;
      }

      this.formDialogVisible = false;
      await this.fetchPurchase();
      this.successMessage = 'Achat suspendu en brouillon.';
    },

    async handlePurchaseFinish(syncedPurchase) {
      if (syncedPurchase) {
        this.purchase = syncedPurchase;
      }

      this.formDialogVisible = false;
      await this.fetchPurchase();
      await this.validatePurchase();
    },

    async handleSave(payload) {
      if (!this.purchase) return;
      try {
        await this.$api.put(`/purchases/${this.purchase.id}`, payload);
        this.successMessage = 'Achat mis à jour avec succès.';
        this.formDialogVisible = false;
        await this.fetchPurchase();
      } catch (error) {
        console.error('Erreur mise à jour achat', error);
        let msg =
          "Une erreur est survenue lors de la mise à jour de l'achat.";
        if (
          error &&
          error.response &&
          error.response.data &&
          error.response.data.errors
        ) {
          const rawErrors = Object.values(error.response.data.errors);
          const flatMessages = rawErrors.reduce(
            (acc, val) => acc.concat(val),
            []
          );
          msg = flatMessages.join(' ');
        }
        this.errorMessage = msg;
      }
    },

    async validatePurchase() {
      if (!this.purchase || this.purchase.status !== 'draft') return;
      if (!this.hasPurchaseItems) {
        this.errorMessage = 'Ajoutez au moins une ligne d’achat avant de valider.';
        return;
      }

      this.errorMessage = '';
      this.successMessage = '';

      try {
        const response = await this.$api.get(
          `/purchases/${this.purchase.id}/supplier-price-changes`
        );
        const changes = response.data?.data?.changes || [];

        if (changes.length > 0) {
          this.supplierPriceChanges = changes;
          this.supplierPriceChangesDialogVisible = true;
          return;
        }
      } catch (error) {
        console.error('Erreur prévisualisation prix fournisseur', error);
        this.errorMessage = this.extractErrorMessage(
          error,
          "Impossible de vérifier les prix fournisseur avant validation."
        );
        return;
      }

      const ok = await this.$confirmAction(
        `Valider l'achat "${this.purchase.reference}" ? Le stock sera mis à jour.`
      );
      if (!ok) return;

      await this.submitValidation(false, []);
    },

    async confirmValidationWithSupplierPrices(payload) {
      await this.submitValidation(
        payload.updateSupplierPrices,
        payload.productIds
      );
    },

    async submitValidation(updateSupplierPrices, productIds) {
      if (!this.purchase || this.purchase.status !== 'draft') return;

      this.isValidating = true;
      try {
        await this.$api.post(`/purchases/${this.purchase.id}/validate`, {
          update_supplier_prices: updateSupplierPrices,
          supplier_price_product_ids: productIds || [],
        });
        this.supplierPriceChangesDialogVisible = false;
        this.supplierPriceChanges = [];
        this.successMessage = 'Achat validé avec succès.';
        await this.fetchPurchase();
      } catch (error) {
        console.error('Erreur validation achat', error);
        this.errorMessage = this.extractErrorMessage(
          error,
          "Une erreur est survenue lors de la validation de l'achat."
        );
      } finally {
        this.isValidating = false;
      }
    },

    async deletePurchase() {
      if (!this.purchase || this.purchase.status !== 'draft') return;
      if (this.isDeletingPurchase) return;

      this.isDeletingPurchase = true;

      const ok = await this.$confirmAction(
        `Supprimer l'achat "${this.purchase.reference}" ?`
      );
      if (!ok) {
        this.isDeletingPurchase = false;
        return;
      }

      try {
        await this.$api.delete(`/purchases/${this.purchase.id}`);
        this.successMessage = 'Achat supprimé.';
        this.$router.push({ name: 'Purchases' });
      } catch (error) {
        console.error('Erreur suppression achat', error);
        let msg =
          "Une erreur est survenue lors de la suppression de l'achat.";
        if (
          error &&
          error.response &&
          error.response.data &&
          error.response.data.errors
        ) {
          const rawErrors = Object.values(error.response.data.errors);
          const flatMessages = rawErrors.reduce(
            (acc, val) => acc.concat(val),
            []
          );
          msg = flatMessages.join(' ');
        }
        this.errorMessage = msg;
      } finally {
        this.isDeletingPurchase = false;
      }
    },

    extractErrorMessage(error, fallback) {
      if (error?.response?.data?.errors) {
        return Object.values(error.response.data.errors).flat().join(' ');
      }

      return fallback;
    },
  },
};
</script>

<style scoped>
.card {
  border-radius: 0.75rem;
}

.purchase-order-preview {
  background: #ffffff;
  color: #212529;
  padding: 1rem;
}

.purchase-order-logo {
  max-height: 72px;
  max-width: 180px;
  object-fit: contain;
}

@media print {
  :global(body.suite241-printing-purchase-order *) {
    visibility: hidden;
  }

  :global(body.suite241-printing-purchase-order #printable-purchase-order),
  :global(body.suite241-printing-purchase-order #printable-purchase-order *) {
    visibility: visible;
  }

  :global(body.suite241-printing-purchase-order #printable-purchase-order) {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    margin: 0;
  }

  @page {
    margin: 10mm;
  }
}
</style>
